import React, { useState } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import Grid from "../assets/images/grid_icon.svg";
import List from "../assets/images/list_icon.svg";

// Store for cart management
import { useCartStore } from "../store/cartStore";

// Auth context and utilities
import { useAuth } from "../contexts/AuthContext";
import {
  canUserPurchase,
  createLoginRedirectUrl,
  getRoleName,
} from "../utils/authUtils";

// Components
import PurchaseProtection from "../components/PurchaseProtection";
import ProductCarousel from "../components/ProductCarousel";

// Demo Requirement #4: Basic ecommerce site with products and services
const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [sortBy, setSortBy] = useState("name"); // name, price-low, price-high
  const { addToCart, cart } = useCartStore();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Default placeholder image as inline SVG to avoid network errors
  const defaultProductImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext fill='%236b7280' font-family='Arial, sans-serif' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EProduct Image%3C/text%3E%3C/svg%3E";

  // Fetch products
  const {
    data: productsData,
    isLoading,
    error,
  } = useQuery(
    ["products", selectedCategory],
    async () => {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }

      const response = await axios.get(`/api/products?${params.toString()}`);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  // Fetch categories
  const { data: categoriesData } = useQuery(
    "categories",
    async () => {
      const response = await axios.get("/api/products/categories/list");
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const products = productsData?.products || [];
  const categories = categoriesData?.categories || [];

  // Filter and sort products
  let filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort products
  if (sortBy === "price-low") {
    filteredProducts = filteredProducts.sort((a, b) => a.price - b.price);
  } else if (sortBy === "price-high") {
    filteredProducts = filteredProducts.sort((a, b) => b.price - a.price);
  } else {
    filteredProducts = filteredProducts.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  const handleAddToCart = (product) => {
    if (isAuthenticated && !canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can add items to cart.`
      );
      return;
    }

    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleExternalPayment = (product) => {
    if (!isAuthenticated) {
      toast.error(`Please login to purchase ${product.name}`);
      const loginUrl = createLoginRedirectUrl(
        location.pathname + location.search
      );
      navigate(loginUrl);
      return;
    }

    if (!canUserPurchase(user)) {
      const userRole = getRoleName(user.role);
      toast.error(
        `${userRole} accounts cannot make purchases. Only customer accounts can purchase products.`
      );
      return;
    }

    window.open(product.payment_link, "_blank");
    toast.success("Redirecting to payment gateway...");
  };

  const getCartItemQuantity = (productId) => {
    const item = cart.find((item) => item.id === productId);
    return item ? item.quantity : 0;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 mb-4">
            We couldn't load the products. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary btn-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Product Carousel Hero Section */}
      <ProductCarousel />

      {/* Main Content */}
      <div
        id="products-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"
      >
        {/* Authentication and Purchase Notifications */}
        {isAuthenticated && !canUserPurchase(user) && (
          <div className="mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-yellow-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  🔒 Purchase Restricted
                </h3>
                <p className="text-yellow-700">
                  You are logged in as{" "}
                  <strong className="font-semibold">
                    {getRoleName(user.role)}
                  </strong>
                  . Only customer accounts can make purchases. You can browse
                  and add items to cart for review.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Professional Filters Section */}
        <div className="mb-12">
          <div className="p-0">
            {/* Top Row: Search + Sort + View + Categories (Products) */}
            <div className="border-b pb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              {/* Search */}
              <div className="w-full md:flex-1 md:max-w-xl">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-500 transition
                               focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-400"
                    placeholder="Search Products & Services"
                  />
                </div>
              </div>

              {/* Sort + View */}
              <div className="w-full md:w-auto flex gap-4 sm:flex-row sm:items-end md:items-center md:flex-none md:gap-6">
                <div className="shrink-0">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-900 font-medium min-w-[160px]
                               focus:outline-none focus:ring-2 focus:ring-fuchsia-300 focus:border-fuchsia-400"
                  >
                    <option value="name">Sort By (A-Z)</option>
                    <option value="price-low">Price (Low to High)</option>
                    <option value="price-high">Price (High to Low)</option>
                  </select>
                </div>

                {/* View Type */}
                <div className="shrink-0">
                  <div className="flex rounded-lg">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`px-1 pt-2 rounded-md text-sm font-semibold transition-colors border border-transparent
                        ${
                          viewMode === "grid"
                            ? "text-fuchsia-600"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      aria-pressed={viewMode === "grid"}
                    >
                      <div className="items-center justify-center mr-3">
                        <img className="w-5" src={Grid} alt="Grid view" />
                      </div>
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`px-1 pt-2 rounded-md text-sm font-semibold transition-colors border border-transparent
                        ${
                          viewMode === "list"
                            ? "text-fuchsia-600"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      aria-pressed={viewMode === "list"}
                    >
                      <div className="items-center justify-center mr-3">
                        <img className="w-6" src={List} alt="List view" />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Products (Categories) — horizontal scroll on small screens */}
                <div className="w-full flex items-center gap-3 overflow-x-auto whitespace-nowrap md:overflow-visible">
                  <button
                    onClick={() => setSelectedCategory("all")}
                    className={`px-0 py-1 text-sm font-medium transition-colors
                      ${
                        selectedCategory === "all"
                          ? "text-fuchsia-600 underline underline-offset-4"
                          : "text-gray-700 hover:text-gray-900 hover:underline underline-offset-4"
                      }`}
                  >
                    All Products
                  </button>

                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-0 py-1 text-sm font-medium transition-colors
                        ${
                          selectedCategory === category
                            ? "text-fuchsia-600 underline underline-offset-4"
                            : "text-gray-700 hover:text-gray-900 hover:underline underline-offset-4"
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div
            className={`grid gap-6 ${
              viewMode === "grid"
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                : "grid-cols-1"
            }`}
          >
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 animate-pulse"
              >
                <div className="h-48 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-5 bg-gray-200 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Products */}
        {!isLoading && (
          <>
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No products found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm
                    ? "Try adjusting your search terms or browse different categories."
                    : "No products available in this category."}
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("all");
                  }}
                  className="px-6 py-3 rounded-lg font-semibold text-white bg-fuchsia-500 hover:bg-fuchsia-600 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <>
                {/* Results Summary */}
                <div className="mb-6 flex items-center justify-between">
                  <p className="text-gray-600">
                    Showing{" "}
                    <span className="font-semibold text-gray-900">
                      {filteredProducts.length}
                    </span>
                    {filteredProducts.length === 1 ? " product" : " products"}
                  </p>
                </div>

                {/* Grid/List */}
                <div
                  className={`grid gap-6 ${
                    viewMode === "grid"
                      ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                      : "grid-cols-1 max-w-4xl mx-auto"
                  }`}
                >
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className={`group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 ${
                        viewMode === "list" ? "flex" : "flex flex-col"
                      }`}
                    >
                      {/* Image */}
                      <Link
                        to={`/products/${product.id}`}
                        className={`relative overflow-hidden block ${
                          viewMode === "list" ? "w-48 flex-shrink-0" : "h-56"
                        }`}
                      >
                        <img
                          src={product.image_url || defaultProductImage}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            if (e.target.src !== defaultProductImage) {
                              e.target.src = defaultProductImage;
                            }
                          }}
                        />

                        {/* Badges */}
                        <div className="absolute top-4 left-4 flex flex-col gap-2">
                          {product.category && (
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-white text-gray-700 shadow-sm border border-gray-200">
                              {product.category}
                            </span>
                          )}
                          {product.payment_link && (
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                              Quick Payment
                            </span>
                          )}
                        </div>

                        {/* Stock Status */}
                        {product.stock_quantity !== undefined && (
                          <div className="absolute top-4 right-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold shadow-sm border ${
                                product.stock_quantity > 0
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : "bg-red-50 text-red-700 border-red-200"
                              }`}
                            >
                              {product.stock_quantity > 0
                                ? `${product.stock_quantity} in stock`
                                : "Out of stock"}
                            </span>
                          </div>
                        )}
                      </Link>

                      {/* Info */}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex-grow">
                          <Link to={`/products/${product.id}`}>
                            <h3 className="text-lg font-normal text-gray-900 mb-2 transition-colors line-clamp-2 hover:text-fuchsia-500 cursor-pointer">
                              {product.name}
                            </h3>
                          </Link>

                          <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                            {product.description ||
                              "Professional solution designed to meet your business needs with exceptional quality and results."}
                          </p>

                          <div className="flex items-center justify-between mb-4">
                            <div className="text-2xl font-medium text-gray-900">
                              ₹{product.price.toLocaleString()}
                            </div>
                          </div>

                          {/* Rating Display */}
                          {product.total_reviews > 0 ? (
                            <div className="flex items-center space-x-1 mb-4">
                              {[...Array(5)].map((_, i) => (
                                <svg
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i <
                                    Math.round(
                                      parseFloat(product.average_rating)
                                    )
                                      ? "text-yellow-400 fill-current"
                                      : "text-gray-300"
                                  }`}
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                              <span className="text-xs text-gray-600 ml-1">
                                {parseFloat(product.average_rating).toFixed(1)}{" "}
                                ({product.total_reviews})
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 mb-4">
                              No reviews yet
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="space-y-2">
                          {product.payment_link ? (
                            <>
                              <PurchaseProtection
                                onPurchaseAction={() =>
                                  handleExternalPayment(product)
                                }
                                productName={product.name}
                                fallbackText="Buy Now"
                              >
                                <button
                                  onClick={() => handleExternalPayment(product)}
                                  className="w-full bg-green-600 text-white py-2 px-3 rounded-lg font-normal hover:bg-green-700 transition-colors duration-200 text-sm"
                                >
                                  Buy Now - Instant Payment
                                </button>
                              </PurchaseProtection>

                              <button
                                onClick={() => handleAddToCart(product)}
                                disabled={product.stock_quantity === 0}
                                className={`w-full py-2 px-3 rounded-lg font-normal transition-colors duration-200 text-sm
                                  ${
                                    product.stock_quantity === 0
                                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                      : getCartItemQuantity(product.id) > 0
                                      ? "bg-purple-600 text-white hover:bg-purple-700"
                                      : "bg-fuchsia-200 text-gray-900 hover:bg-fuchsia-300"
                                  }`}
                              >
                                {getCartItemQuantity(product.id) > 0
                                  ? `In Cart (${getCartItemQuantity(
                                      product.id
                                    )})`
                                  : "Add to Cart"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleAddToCart(product)}
                              disabled={product.stock_quantity === 0}
                              className={`w-full py-2 px-3 rounded-lg font-normal transition-colors duration-200 text-sm
                                ${
                                  product.stock_quantity === 0
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : getCartItemQuantity(product.id) > 0
                                    ? "bg-purple-600 text-white hover:bg-purple-700"
                                    : "bg-fuchsia-200 text-gray-900 hover:bg-fuchsia-300"
                                }`}
                            >
                              {getCartItemQuantity(product.id) > 0
                                ? `In Cart (${getCartItemQuantity(product.id)})`
                                : "Add to Cart"}
                            </button>
                          )}

                          <Link
                            to={`/products/${product.id}`}
                            className="w-full bg-white px-3 py-2 rounded-lg font-light border text-center block text-sm text-gray-700 border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
